/**
 * INCSUBResetOTPPage Component
 * @author govind dindewad
 * This component handles OTP input for the password reset process. It allows users to:
 * - Enter a 6-digit OTP received on their registered phone number.
 * - Automatically navigate through input fields for a smoother experience.
 * - Option to resend OTP (UI only, actual functionality needs integration).
 * - Submit the OTP for verification.
 * 
 * Props:
 * - `userName` (string): The masked phone number to which the OTP is sent.
 * - `fun` (function): Callback function to handle OTP submission.
 * 
 * Features:
 * - Masking of phone number for privacy.
 * - Dynamic input handling (autofocus, backspace navigation and joins array input).
 * 
 * Dependencies:
 * - `styleResetOTP.css` for styling.
 */
import React, { useState } from 'react';
import './styleResetOTP.css';

function INCSUBResetOTPPage({ userName, fun }) {
    const [otp, setOtp] = useState(["", "", "", "", "", ""]); // State to manage OTP input fields.

    /**
     * Handles the input change for each OTP field.
     * Automatically moves focus to the next field if input is valid.
     * 
     * @param {string} value - The value entered in the input field.
     * @param {number} index - Index of the current input field.
     * @author govind dindewad
     */
    const handleInputChange = (value, index) => {
        const newOtp = [...otp];
        newOtp[index] = value; // Update the value for the current index.
        setOtp(newOtp);

        // Automatically focus the next input if value is entered and it's not the last field.
        if (value && index < 5) {
            document.getElementById(`otp-${index + 1}`).focus();
        }
    };

    /**
     * Handles key down events for OTP fields.
     * Moves focus to the previous field on Backspace if the current field is empty.
     * 
     * @param {object} event - Keyboard event.
     * @param {number} index - Index of the current input field.
     * @author govind dindewad
     */
    const handleKeyDown = (event, index) => {
        if (event.key === "Backspace" && otp[index] === "" && index > 0) {
            document.getElementById(`otp-${index - 1}`).focus();
        }
    };

    /**
     * Masks the phone number for privacy.
     * Only displays the last two digits.
     * 
     * @param {string} phoneNumber - The phone number to mask.
     * @returns {string} - Masked phone number.
     * @author govind dindewad
     */
    function maskPhoneNumber(phoneNumber) {
        const str = phoneNumber.toString();
        return "********" + str.slice(-2); // Display only the last two digits.
    }

    return (
        <div className='INCSUBResetOTPpage'>
            <div className='INCSUBResetOTPpage_container'>
                <div className='INCSUBResetOTPinfo_container'>
                    <div className='INCSUBResetOTPpage_headerinfo_container'>
                        <div className='INCSUBResetOTPpage_header'>
                            Enter Verification Code
                        </div>
                        <div className='INCSUBResetOTPpage_subheader'>
                            A 6-digit code was sent to {maskPhoneNumber(userName)}
                        </div>
                    </div>
                    <div className='INCSUBResetOTPForm_container'>
                        <form
                            className='INCSUBResetOTP_form'
                            onSubmit={(e) => e.preventDefault()} // Prevent form submission.
                        >
                            <div className='INCSUBUsercred_container'>
                                {otp.map((value, index) => (
                                    <input
                                        key={index}
                                        id={`otp-${index}`}
                                        type="text"
                                        value={value}
                                        onChange={(e) => handleInputChange(e.target.value.slice(-1), index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        maxLength="1" // Limit input to one character.
                                        style={{
                                            width: "50px",
                                            height: "50px",
                                            textAlign: "center",
                                            fontSize: "18px",
                                            border: "1px solid #ccc",
                                            borderRadius: "8px",
                                        }}
                                    />
                                ))}
                            </div>
                            <div className='INCSUBResetOTPForm_submitbutton_container'>
                                <p style={{ cursor: "pointer" }}>
                                    Resend code
                                </p>
                                <button
                                    type='button'
                                    className='INCSUBResetOTPForm_submitbutton'
                                    onClick={() => fun(otp.join(""))} // Pass concatenated OTP to callback.
                                >
                                    Set New Password
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default INCSUBResetOTPPage;
